import React, { FC, useState, useEffect } from 'react';
import './styles.scss';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  ToastMessage,
  Input
} from '@cokitchen/cokitchen-components';
import { getSimpleDateFormat, formatTime, getFilterName } from 'utils/functions';
import { useLocation, useNavigate } from 'react-router-dom';
import { MealCategoryType, SearchType } from 'utils/types';
import { ReactComponent as EditMealIcon } from 'assets/icons/edit-meal-icon.svg';
import { ReactComponent as DuplicateMealIcon } from 'assets/icons/duplicate-meal-icon.svg';
import { ReactComponent as ConvertMealIcon } from 'assets/icons/convert-meal-icon.svg';
import { ReactComponent as DeleteMealIcon } from 'assets/icons/delete-meal-icon.svg';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { MenuHorizontalIcon } from 'cokitchen-icons';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import { ENUM_CATEGORY, ENUM_SEARCH_TYPE } from 'utils/enum';
import useDashboardContext from 'contexts/dashboard.context';
import { ReactComponent as RestoreIcon } from 'assets/icons/restore.svg';
import EditMealCategoryModal from 'components/edit-meal-category-modal';
import DuplicateMealCategoryModal from 'components/duplicate-meal-category-modal';
import DeleteModal from 'components/base/delete-modal';

type Props = {
  data: MealCategoryType | SearchType;
  className?: string;
  checked?: boolean;
  onChange?: () => void;
  onRestoreComplete?: (arg0: string) => void;
  selectedItems?: string[];
  refreshData?: () => void;
};

type OptionsType = {
  onClick?: () => void;
  name: string;
  icon: JSX.Element;
  className?: string;
};

const MealCategoryCard: FC<Props> = ({
  data,
  className,
  selectedItems,
  onChange,
  onRestoreComplete,
  refreshData
}) => {
  const { trash } = useDashboardContext();
  const { category } = useDashboardContext();
  const [cardHovered, setCardHovered] = useState(false);
  const [showEditMealCategoryModal, setShowEditMealCategoryModal] = useState(false);
  const [showDuplicateMealCategoryModal, setShowDuplicateMealCategoryModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteMealCategoryModal, setshowDeleteMealCategoryModal] = useState(false);
  const [showRestoreCategoryModal, setShowRestoreCategoryModal] = useState(false);

  const navigate = useNavigate();

  const editMealCategory = () => {
    setShowEditMealCategoryModal(!showEditMealCategoryModal);
  };
  const duplicateMealCategory = () => {
    setShowDuplicateMealCategoryModal(!showDuplicateMealCategoryModal);
  };
  const deleteMealCategory = () => {
    setshowDeleteMealCategoryModal(!showDeleteMealCategoryModal);
  };
  const convertMealCategory = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/convert-to-addon-category/${data.id}`, {
      state: data.name
    });
  };
  const restoreCategory = () => {
    setShowRestoreCategoryModal(!showRestoreCategoryModal);
  };

  const { pathname } = useLocation();
  const isSearch = () => {
    return pathname.includes('search');
  };

  const searchFilter = pathname.split('/')[4];
  const filterName = getFilterName(pathname);
  const editName = isSearch() ? `Edit ${filterName}` : 'Edit meal category';
  const deleteName = isSearch() ? `Delete ${filterName}` : 'Delete meal category';

  const baseOptions = [
    { onClick: editMealCategory, name: editName, icon: <EditMealIcon /> },
    { onClick: convertMealCategory, name: 'Convert meal category', icon: <ConvertMealIcon /> },
    {
      onClick: duplicateMealCategory,
      name: 'Duplicate meal category',
      icon: <DuplicateMealIcon />
    },
    {
      onClick: deleteMealCategory,
      name: deleteName,
      icon: <DeleteMealIcon />,
      className: 'text-[#E60A2B]'
    }
  ];

  const archivedOptions = [
    { onClick: restoreCategory, name: 'Restore Category', icon: <RestoreIcon /> }
  ];
  const getOptions = (): OptionsType[] => {
    if (!trash) {
      return baseOptions;
    }
    return archivedOptions;
  };

  const handleDeleteClick = async () => {
    try {
      setLoading(true);
      let url = '';
      let payload = {};
      let method: 'delete' | 'post' = 'post';
      if (data.brand_id) {
        url = `meal-category/${data?.id}`;
        method = 'delete';
      } else {
        url = 'marketing/bulk-delete-meal-addon-category';
        payload = { meal_addon_category_ids: [data.id] };
        method = 'post';
      }
      await apiService[method](url, { data: payload });

      toast.success(<ToastMessage type='success' message={'Item deleted successfully'} />);
      setshowDeleteMealCategoryModal(false);
    } catch {
      toast(<ToastMessage type='error' message='Error occur while deleting item' />);
    } finally {
      setLoading(false);
    }
  };

  const mealUrl = `${ROUTE_PREFIX.FOOD}/${[ROUTE_KEYS.MEALS, data?.id].join('/')}${
    trash ? '?trash=true' : ''
  }`;
  const addonUrl = `${ROUTE_PREFIX.FOOD}/${[
    ROUTE_KEYS.MEALS,
    data?.meal_parent_id,
    'addon-categories'
  ].join('/')}`;

  useEffect(() => {
    if (isSearch()) {
      setCardHovered(true);
    }
  }, [pathname]);

  const toggleCardHovered = (value: boolean) => {
    if (!isSearch()) {
      setCardHovered(value);
    }
  };
  const handleRestoreClick = async () => {
    const payload = { type: 'meal_category', item_ids: [data.id] };
    try {
      const res: any = await apiService.post('marketing/restore-bulk-items', { data: payload });
      if (res.data) {
        toast(<ToastMessage type='success' message={`Meal Category restored successfully `} />);
        restoreMealCategory();
        onRestoreComplete?.(data?.id);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  const getName = () => {
    let name = '';
    const searchFilter = pathname.split('/')[4];
    const isFood = category === ENUM_CATEGORY.FOOD;
    const isAddonCat = searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY;
    if (isFood) {
      name = 'meals';
      if (isAddonCat) {
        name = 'addon';
      }
    } else {
      name = 'items';
    }
    return name;
  };

  const onLinkClick = () => {
    if (searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY) {
      navigate(addonUrl, { state: data.id });
    } else {
      navigate(mealUrl);
    }
  };
  const restoreMealCategory = () => {
    setShowRestoreCategoryModal(!showRestoreCategoryModal);
  };

  return (
    <div
      onMouseEnter={() => toggleCardHovered(true)}
      onMouseLeave={() => toggleCardHovered(false)}
      className={`meal-category py-[14px] px-[20px] rounded border border-[#FAFAFA] relative ${className}`}>
      <div>
        <div className=' flex justify-between items-start'>
          <div>
            <p className='leading-[22px] text-sm font-medium'>{data?.name}</p>
            <p className='pb-[20px] text-[#858585] text-xs'>
              Created&nbsp;
              <span className='mr-[4px]'>{getSimpleDateFormat(data.created_at ?? '')},</span>
              <span>{formatTime(new Date(data.created_at ?? ''))}</span>
            </p>
          </div>

          <div>
            {data?.horizontal && (
              <div
                style={{ borderRadius: '0 4px 0px 4px' }}
                className='bg-[#46AE31] absolute top-0 right-0  text-white text-[10px] rounded-full w-fit px-2 py-[2px]  '>
                <p> Horizontal</p>
              </div>
            )}
            {(cardHovered || selectedItems?.includes(data.id)) && (
              <div className='hidden lg:flex gap-4 items-center'>
                {!selectedItems?.length ? (
                  <Dropdown
                    content={
                      <MenuHorizontalIcon
                        className={`cursor-pointer w-[22px] h-[22px] text-white bg-black hover:bg-black opacity-20 rounded p-[3px] transition-none z-10`}
                      />
                    }>
                    <DropdownList>
                      {getOptions().map(({ icon, onClick, name, className }, index) => (
                        <DropdownItem
                          key={index}
                          onClick={onClick}
                          className='text-sm font-light  hover:bg-gray-400 cursor-pointer'
                          buttonClassName='p-2'>
                          <div className='flex justify-start gap-3 items-center'>
                            <div className='list-icon rounded-full p-1  border-[0.6px]  border-[#EAEAEB]'>
                              {icon}
                            </div>
                            <p className={`${className}`}>{name}</p>
                          </div>
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  </Dropdown>
                ) : null}
                {(isSearch() || data?.is_deleted) && (
                  <Input
                    type='checkbox'
                    name='new'
                    checked={selectedItems?.includes(data.id)}
                    checkColor='gray'
                    onChange={onChange}
                    className=''
                  />
                )}
              </div>
            )}
          </div>
          <div className='flex lg:hidden gap-4 items-center'>
            {isSearch() && (
              <Input
                type='checkbox'
                name='new'
                checked={selectedItems?.includes(data.id)}
                checkColor='gray'
                onChange={onChange}
                className=''
              />
            )}

            {selectedItems?.length === 0 && (
              <Dropdown
                content={
                  <MenuHorizontalIcon
                    className={`cursor-pointer w-[22px] h-[22px] text-white bg-black hover:bg-black opacity-20 rounded p-[3px] transition-none z-10`}
                  />
                }>
                <DropdownList>
                  {getOptions().map(({ icon, onClick, name, className }, index) => (
                    <DropdownItem
                      key={index}
                      onClick={onClick}
                      className='text-sm font-light  hover:bg-gray-400 cursor-pointer'
                      buttonClassName='p-2'>
                      <div className='flex justify-start gap-3 items-center'>
                        <div className='list-icon rounded-full p-1  border-[0.6px]  border-[#EAEAEB]'>
                          {icon}
                        </div>
                        <p className={`${className}`}>{name}</p>
                      </div>
                    </DropdownItem>
                  ))}
                </DropdownList>
              </Dropdown>
            )}
          </div>
        </div>
        {!trash && (
          <div onClick={onLinkClick}>
            <p className='font-medium text-[#E60A2B] text-xs underline cursor-pointer'>
              View {getName()}
            </p>
          </div>
        )}
      </div>

      <EditMealCategoryModal
        refreshData={refreshData}
        show={showEditMealCategoryModal}
        onClose={editMealCategory}
        mealCategory={{
          horizontal: data.horizontal ?? false,
          name: data.name,
          meal_category_id: data.id,
          description: data.summary ?? ''
        }}
      />
      <DuplicateMealCategoryModal
        show={showDuplicateMealCategoryModal}
        onClose={duplicateMealCategory}
        mealCategory={{
          name: data.name,
          meal_category_id: data.id
        }}
      />
      <DeleteModal
        show={showDeleteMealCategoryModal}
        onClose={deleteMealCategory}
        handleDelete={() => handleDeleteClick()}
        title={`Are you sure you want to archive ${data?.name} ?`}
        description='This action cannot be undone'
        loading={loading}
      />
      <DeleteModal
        show={showRestoreCategoryModal}
        onClose={restoreMealCategory}
        handleDelete={() => handleRestoreClick()}
        title={`Are you sure you want to restore ${data?.name} ?`}
        description='This action will restore this category'
        loading={loading}
        type='restore'
      />
    </div>
  );
};

export default MealCategoryCard;
